import React, { useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';
import { APP_URLS } from '../../helpers/url_helper';
import StandaloneTopbar from '../../components/StandaloneTopbar';
import './StripePayment.css';

// Import assets
import checkmarkIcon from '../../assets/checkmark.svg';

function StandalonePaymentSuccess() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessionData, setSessionData] = useState(null);
  const [paymentProcessed, setPaymentProcessed] = useState(false);
  const [loading, setLoading] = useState(true);

  // Initialize theme on component mount
  useEffect(() => {
    document.body.classList.remove('dark-mode', 'light-mode');
    document.body.classList.add(mode === 'dark' ? 'dark-mode' : 'light-mode');
  }, [mode]);

  // Get session details from URL params and process payment success
  useEffect(() => {
    const processPaymentSuccess = async () => {
      let sessionId = searchParams.get('session_id');
      const planName = searchParams.get('plan');
      const billingCycle = searchParams.get('billing');
      
      console.log('=== STRIPE PAYMENT SUCCESS - INITIAL DATA ===');
      console.log('Session ID from URL:', sessionId);
      console.log('Plan from URL:', planName);
      console.log('Billing Cycle from URL:', billingCycle);
      console.log('All URL Params:', Object.fromEntries(searchParams));
      
      // If no session_id in URL, try to get from localStorage
      if (!sessionId) {
        const storedSessionId = localStorage.getItem('pendingStripeSessionId');
        if (storedSessionId) {
          sessionId = storedSessionId;
          console.log('=== USING STORED SESSION ID ===');
          console.log('Retrieved session_id from localStorage:', sessionId);
          // Clear the stored session_id since we're using it now
          localStorage.removeItem('pendingStripeSessionId');
        }
      }

      if (sessionId && !paymentProcessed) {
        try {
          console.log('=== PROCESSING PAYMENT SUCCESS ===');
          console.log('Session ID:', sessionId);
          
          // Get stored plan details
          const storedPlanDetails = localStorage.getItem('pendingPlanDetails');
          let planDetails = null;
          if (storedPlanDetails) {
            try {
              planDetails = JSON.parse(storedPlanDetails);
              console.log('Retrieved plan details from localStorage:', planDetails);
            } catch (err) {
              console.error('Error parsing stored plan details:', err);
            }
          }

          // Set session data for display
          setSessionData({
            sessionId: sessionId,
            planName: planName || planDetails?.planTitle || 'Selected Plan',
            billingCycle: billingCycle || planDetails?.billingCycle || 'monthly',
            displayPrice: planDetails?.displayPrice || 'N/A'
          });

          setPaymentProcessed(true);
          setLoading(false);
          
          // Clear stored data
          localStorage.removeItem('pendingPlanDetails');
          
          console.log('=== PAYMENT SUCCESS PROCESSED ===');
          console.log('Session Data:', {
            sessionId: sessionId,
            planName: planName || planDetails?.planTitle || 'Selected Plan',
            billingCycle: billingCycle || planDetails?.billingCycle || 'monthly',
            displayPrice: planDetails?.displayPrice || 'N/A'
          });
          
        } catch (error) {
          console.error('Error processing payment success:', error);
          setLoading(false);
        }
      } else {
        console.log('=== NO SESSION ID OR ALREADY PROCESSED ===');
        console.log('Session ID:', sessionId);
        console.log('Payment Processed:', paymentProcessed);
        setLoading(false);
      }
    };

    processPaymentSuccess();
  }, [searchParams, paymentProcessed]);

  const handleGoToDashboard = () => {
    navigate(APP_URLS.DASHBOARD);
  };

  if (loading) {
    return (
      <div className="standalone-page">
        <StandaloneTopbar />
        <div className="payment-result-page" style={{ marginTop: '60px' }}>
          <div className="payment-success-centered-layout">
            <div className="success-header-centered">
              <h1 className="payment-title success-title">
                <img src={checkmarkIcon} alt="Success" className="success-main-icon" />
                Payment Successful!
              </h1>
              <p className="payment-message">
                Thank you for your subscription! Your payment has been processed successfully.
              </p>
            </div>

            <div className="success-boxes-container">
              <div className="success-box next-steps-box">
                <h5 className="next-steps-title">What's Next?</h5>
                <ul className="next-steps-list">
                  <li>Access all premium features immediately</li>
                  <li>Check your email for the subscription confirmation</li>
                  <li>Start managing your compliance projects</li>
                </ul>
              </div>

              <div className="success-box subscription-summary-box">
                <h4 className="summary-title">Subscription Summary</h4>
                <div className="loading-container">
                  <Spinner animation="border" role="status" className="loading-spinner">
                    <span className="visually-hidden">Loading subscription details...</span>
                  </Spinner>
                  <p className="loading-text">Loading your subscription details...</p>
                </div>
              </div>
            </div>

            <div className="success-actions-centered">
              <Button 
                variant="primary" 
                className="action-btn primary-action"
                onClick={handleGoToDashboard}
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="standalone-page">
      <StandaloneTopbar />
      <div className="payment-result-page" style={{ marginTop: '60px' }}>
        <div className="payment-success-centered-layout">
          {/* Centered Header */}
          <div className="success-header-centered">
            <h1 className="payment-title success-title">
              <img src={checkmarkIcon} alt="Success" className="success-main-icon" />
              Payment Successful!
            </h1>
            <p className="payment-message">
              Thank you for your subscription! Your payment has been processed successfully.
            </p>
          </div>

          {/* Two Boxes Side by Side */}
          <div className="success-boxes-container">
            {/* What's Next Box */}
            <div className="success-box next-steps-box">
              <h5 className="next-steps-title">What's Next?</h5>
              <ul className="next-steps-list">
                <li>Access all premium features immediately</li>
                <li>Check your email for the subscription confirmation</li>
                <li>Start managing your compliance projects</li>
              </ul>
            </div>

            {/* Subscription Summary Box */}
            <div className="success-box subscription-summary-box">
              <h4 className="summary-title">Subscription Summary</h4>
              
              {/* Loading spinner while waiting for data */}
              {loading && (
                <div className="loading-container">
                  <Spinner animation="border" role="status" className="loading-spinner">
                    <span className="visually-hidden">Loading subscription details...</span>
                  </Spinner>
                  <p className="loading-text">Loading your subscription details...</p>
                </div>
              )}

              {/* Error state - only show if there's an error and we're not loading */}
              {!loading && sessionData && sessionData.error && (
                <div className="error-container">
                  <p className="error-message">
                    There was an issue retrieving your subscription details, but your payment was successful.
                  </p>
                </div>
              )}

              {/* Subscription Details from URL Parameters */}
              {!loading && sessionData && (
                <>
                  <div className="summary-item">
                    <span className="summary-label">Plan:</span>
                    <span className="summary-value">{sessionData.planName}</span>
                  </div>
                  
                  <div className="summary-item">
                    <span className="summary-label">Billing Cycle:</span>
                    <span className="summary-value">
                      {sessionData.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
                    </span>
                  </div>
                  
                  <div className="summary-item">
                    <span className="summary-label">Status:</span>
                    <span className="summary-value">
                      <span className="auto-renewal-status">Payment Successful</span>
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Centered Action Buttons */}
          <div className="success-actions-centered">
            <Button 
              variant="primary" 
              className="action-btn primary-action"
              onClick={handleGoToDashboard}
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StandalonePaymentSuccess;
